import React, {useState, useEffect} from 'react'
import {useNavigate} from 'react-router-dom'
import {
  Search,
  Plus,
  Filter,
  Download,
  RefreshCw,
  MoreHorizontal,
  Edit,
  Trash2,
  Loader2,
  FileText,
  ArrowUpRight,
  ArrowDownRight,
} from 'lucide-react'
import {getAccountVendorCredits, deleteAccountVendorCredit} from '../core/_requests'
import {useAuth} from '../../auth'
import {ACCOUNTS_PERMS} from '../core/permissions'
import {
  AccountsActionDropdown,
  DropdownPosition,
  getDropdownPosition,
} from './AccountsActionDropdown'

interface VendorCredit {
  id: number
  vendorId: number | null
  vendorName: string | null
  creditNoteNumber: string
  referenceNumber: string | null
  creditDate: string | null
  total: number | null
  balance: number | null
  status: string | null
}

export default function AccountsPurchaseVendorCredits() {
  const navigate = useNavigate()
  const {hasPermission} = useAuth()
  const [searchQuery, setSearchQuery] = useState('')
  const [activeFilter, setActiveFilter] = useState('All')
  const [loading, setLoading] = useState(true)
  const [credits, setCredits] = useState<VendorCredit[]>([])
  const [activeDropdown, setActiveDropdown] = useState<number | null>(null)
  const [dropdownPos, setDropdownPos] = useState<DropdownPosition | null>(null)
  const [refreshTrigger, setRefreshTrigger] = useState(0)

  const filters = ['All', 'Open', 'Closed']

  useEffect(() => {
    setLoading(true)
    getAccountVendorCredits()
      .then((data) => setCredits(data || []))
      .catch((err) => console.error('Failed to load vendor credits:', err))
      .finally(() => setLoading(false))
  }, [refreshTrigger])

  useEffect(() => {
    const closeDropdowns = () => {
      setActiveDropdown(null)
      setDropdownPos(null)
    }
    document.addEventListener('click', closeDropdowns)
    return () => document.removeEventListener('click', closeDropdowns)
  }, [])

  const handleRefresh = () => setRefreshTrigger((prev) => prev + 1)

  const handleDelete = async (e: React.MouseEvent, id: number) => {
    e.stopPropagation()
    if (!window.confirm('Are you sure you want to delete this vendor credit?')) return
    try {
      await deleteAccountVendorCredit(id)
      setCredits((prev) => prev.filter((c) => c.id !== id))
    } catch (err: any) {
      console.error('Failed to delete vendor credit:', err)
      alert('Error deleting vendor credit')
    }
  }

  const safeNumber = (val: any): number => {
    let num = Number(val)
    if (isNaN(num)) {
      const clean = typeof val === 'string' ? val.replace(/[^0-9.-]/g, '') : ''
      num = Number(clean)
      if (isNaN(num)) num = 0
    }
    return num
  }

  // Dynamic stats
  const totalCredits = credits.reduce((acc, c) => acc + safeNumber(c.total), 0)
  const totalBalance = credits.reduce((acc, c) => acc + safeNumber(c.balance), 0)
  const openCount = credits.filter((c) => c.status === 'Open').length

  const stats = [
    {
      label: 'Total Credit Value',
      value: new Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD'}).format(
        safeNumber(totalCredits)
      ),
      color: 'blue',
      trend: 'up',
      change: '+0%',
    },
    {
      label: 'Available Balance',
      value: new Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD'}).format(
        safeNumber(totalBalance)
      ),
      color: 'emerald',
      trend: 'up',
      change: '+0%',
    },
    {
      label: 'Open Credits',
      value: openCount.toString(),
      color: 'amber',
      trend: 'up',
      change: '+0%',
    },
    {
      label: 'Total Notes',
      value: credits.length.toString(),
      color: 'violet',
      trend: 'up',
      change: '+0%',
    },
  ]

  // Filter & search
  const filteredCredits = credits.filter((c) => {
    if (activeFilter !== 'All' && c.status !== activeFilter) return false
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      return (
        c.creditNoteNumber?.toLowerCase().includes(q) ||
        c.vendorName?.toLowerCase().includes(q) ||
        c.referenceNumber?.toLowerCase().includes(q) ||
        c.purchaseOrderNumber?.toLowerCase().includes(q)
      )
    }
    return true
  })

  const getStatusBadgeClass = (status: string | null) => {
    switch (status) {
      case 'Open':
        return 'bg-amber-50 text-amber-700 border-amber-200/50'
      case 'Closed':
        return 'bg-slate-100 text-slate-700 border-slate-200'
      default:
        return 'bg-slate-100 text-slate-600 border-slate-200'
    }
  }

  const handleDownloadCSV = () => {
    if (filteredCredits.length === 0) {
      alert('No vendor credit data to export.')
      return
    }
    const formatCurrency = (val: any) => {
      return new Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD'}).format(
        safeNumber(val)
      )
    }
    const headers = [
      'Sr. No.',
      'Credit Date',
      'Credit Note#',
      'PO#',
      'Vendor',
      'Reference#',
      'Status',
      'Total',
      'Balance',
    ]
    const rows = filteredCredits.map((c, index) => [
      index + 1,
      c.creditDate ? new Date(c.creditDate).toLocaleDateString() : '-',
      c.creditNoteNumber || '-',
      c.purchaseOrderNumber || c.purchase_order_number || '-',
      c.vendorName || '-',
      c.referenceNumber || '-',
      c.status || 'Open',
      formatCurrency(c.total || 0),
      formatCurrency(c.balance || 0),
    ])

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map((val) => `"${String(val).replace(/"/g, '""')}"`).join(',')),
    ].join('\n')

    const blob = new Blob([csvContent], {type: 'text/csv;charset=utf-8;'})
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.setAttribute('href', url)
    link.setAttribute(
      'download',
      `vendor_credits_export_${new Date().toISOString().slice(0, 10)}.csv`
    )
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const c = activeDropdown !== null ? filteredCredits.find((c) => c.id === activeDropdown) : null

  return (
    <>
      <div className='flex h-screen w-full overflow-hidden bg-slate-50'>
        <div id='sidebar-overlay' className='fixed inset-0 bg-black/50 z-40 hidden md:hidden'></div>
        <main className='flex-1 flex flex-col h-full overflow-hidden relative'>
          <div
            className='flex-1 overflow-y-auto overflow-x-hidden p-4 md:p-6 space-y-5'
            id='main-scroll'
          >
            {/* Header */}
            <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3'>
              <div>
                <div className='text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1'>
                  Accounts / Purchase
                </div>
                <h1 className='text-xl font-bold text-slate-900 leading-tight'>Vendor Credits</h1>
                <p className='text-xs text-slate-500'>Manage credits received from vendors</p>
              </div>
              <div className='flex items-center gap-2'>
                <div className='flex items-center gap-2 text-[11px] text-slate-500'>
                  <span>Refresh data</span>
                  <button
                    onClick={handleRefresh}
                    className='p-1 rounded hover:bg-slate-100 transition-colors'
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                  </button>
                </div>
                {hasPermission(ACCOUNTS_PERMS.VENDOR_CREDIT_CREATE) && (
                  <button
                    onClick={() => navigate('/accounts/purchase/vendor-credits/new')}
                    className='px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-bold shadow-sm hover:shadow-md transition-all flex items-center gap-2'
                  >
                    <Plus className='w-4 h-4' />
                    New Vendor Credit
                  </button>
                )}
              </div>
            </div>

            {/* Stats */}
            <div className='grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4'>
              {stats.map((stat, i) => (
                <div
                  key={i}
                  className='card-hover bg-white rounded-2xl p-5 border border-slate-200 shadow-sm'
                >
                  <div className='flex items-center justify-between mb-3'>
                    <div
                      className={`w-9 h-9 rounded-xl bg-${stat.color}-50 flex items-center justify-center`}
                    >
                      <FileText className={`w-4.5 h-4.5 text-${stat.color}-600`} />
                    </div>
                    <span
                      className={`text-[10px] font-semibold flex items-center gap-0.5 ${
                        stat.trend === 'up' ? 'text-emerald-600' : 'text-red-500'
                      }`}
                    >
                      {stat.trend === 'up' ? (
                        <ArrowUpRight className='w-3 h-3' />
                      ) : (
                        <ArrowDownRight className='w-3 h-3' />
                      )}
                      {stat.change}
                    </span>
                  </div>
                  <div className='text-2xl font-bold text-slate-900'>{stat.value}</div>
                  <div className='text-[11px] text-slate-500 mt-0.5'>{stat.label}</div>
                </div>
              ))}
            </div>

            {/* Table */}
            <div className='bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden'>
              <div className='p-4 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3'>
                <div className='flex items-center gap-2 overflow-x-auto scrollbar-hide'>
                  {filters.map((f) => (
                    <button
                      key={f}
                      onClick={() => setActiveFilter(f)}
                      className={`px-3 py-1.5 text-[11px] font-bold rounded-lg whitespace-nowrap transition-all ${
                        activeFilter === f
                          ? 'bg-slate-900 text-white'
                          : 'text-slate-500 hover:bg-slate-100'
                      }`}
                    >
                      {f}
                    </button>
                  ))}
                </div>
                <div className='flex items-center gap-2'>
                  <div className='relative'>
                    <Search className='w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400' />
                    <input
                      type='text'
                      placeholder='Search credits...'
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className='pl-9 pr-4 py-2 w-56 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all'
                    />
                  </div>
                  {/* Filter button kept for future use.
                  <button className="p-2 rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors">
                    <Filter className="w-4 h-4 text-slate-500" />
                  </button>
                  */}
                  <button
                    onClick={handleDownloadCSV}
                    className='p-2 rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors'
                  >
                    <Download className='w-4 h-4 text-slate-500' />
                  </button>
                </div>
              </div>

              <div className='overflow-x-auto min-h-[300px]'>
                <table className='w-full text-left min-w-175'>
                  <thead>
                    <tr className='text-[10px] text-slate-400 uppercase tracking-wider bg-slate-50/50'>
                      <th className='px-4 py-3 font-medium text-center text-slate-400'>Sr. No.</th>
                      <th className='px-4 py-3 font-medium'>Credit Date</th>
                      <th className='px-4 py-3 font-medium'>Credit Note#</th>
                      <th className='px-4 py-3 font-medium'>PO#</th>
                      <th className='px-4 py-3 font-medium'>Vendor</th>
                      <th className='px-4 py-3 font-medium'>Reference#</th>
                      <th className='px-4 py-3 font-medium'>Status</th>
                      <th className='px-4 py-3 font-medium text-right'>Total</th>
                      <th className='px-4 py-3 font-medium text-right'>Balance</th>
                      <th className='px-4 py-3 font-medium'></th>
                    </tr>
                  </thead>
                  <tbody className='divide-y divide-slate-100 text-sm'>
                    {loading ? (
                      <tr>
                        <td colSpan={10} className='p-16 text-center'>
                          <Loader2 className='w-8 h-8 text-blue-500 animate-spin mx-auto mb-4' />
                          <p className='text-slate-500 font-medium'>Loading vendor credits...</p>
                        </td>
                      </tr>
                    ) : filteredCredits.length === 0 ? (
                      <tr>
                        <td colSpan={10}>
                          <div className='p-16 flex flex-col items-center justify-center'>
                            <div className='w-20 h-20 bg-linear-to-br from-blue-50 to-blue-100 text-blue-500 rounded-2xl flex items-center justify-center mb-5'>
                              <FileText className='w-10 h-10' strokeWidth={1.5} />
                            </div>
                            <h3 className='text-lg font-bold text-slate-900 mb-2'>
                              {searchQuery ? 'No matching credits' : 'No vendor credits found'}
                            </h3>
                            <p className='text-slate-500 text-sm text-center max-w-sm mb-6'>
                              Record credits received from your vendors here.
                            </p>
                            {!searchQuery && hasPermission(ACCOUNTS_PERMS.VENDOR_CREDIT_CREATE) && (
                              <button
                                onClick={() => navigate('/accounts/purchase/vendor-credits/new')}
                                className='px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-bold shadow-sm hover:shadow-md transition-all flex items-center gap-2'
                              >
                                <Plus className='w-4 h-4' />
                                Record Vendor Credit
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ) : (
                      filteredCredits.map((c, index) => (
                        <tr
                          key={c.id}
                          className='hover:bg-slate-50 transition-colors group cursor-pointer'
                          onClick={() =>
                            hasPermission(ACCOUNTS_PERMS.VENDOR_CREDIT_UPDATE) &&
                            navigate(`/accounts/purchase/vendor-credits/${c.id}`)
                          }
                        >
                          <td
                            className='px-4 py-3 text-slate-500 font-medium text-center'
                            onClick={(e) => e.stopPropagation()}
                          >
                            {index + 1}
                          </td>
                          <td className='px-4 py-3 text-slate-600'>
                            {c.creditDate ? new Date(c.creditDate).toLocaleDateString() : '-'}
                          </td>
                          <td className='px-4 py-3 font-semibold text-blue-600 hover:underline'>
                            {c.creditNoteNumber}
                          </td>
                          <td className='px-4 py-3 text-slate-600'>
                            {c.purchaseOrderNumber || c.purchase_order_number || '-'}
                          </td>
                          <td className='px-4 py-3 font-medium text-slate-700'>
                            {c.vendorName || '-'}
                          </td>
                          <td className='px-4 py-3 text-slate-600'>{c.referenceNumber || '-'}</td>
                          <td className='px-4 py-3'>
                            <span
                              className={`inline-block px-2.5 py-1 text-[10px] font-bold rounded-full border ${getStatusBadgeClass(
                                c.status
                              )}`}
                            >
                              {c.status || 'Open'}
                            </span>
                          </td>
                          <td className='px-4 py-3 font-semibold text-slate-900 text-right'>
                            {new Intl.NumberFormat('en-US', {
                              style: 'currency',
                              currency: 'USD',
                            }).format(safeNumber(c.total))}
                          </td>
                          <td className='px-4 py-3 font-semibold text-emerald-600 text-right'>
                            {new Intl.NumberFormat('en-US', {
                              style: 'currency',
                              currency: 'USD',
                            }).format(safeNumber(c.balance))}
                          </td>
                          <td
                            className='px-4 py-3 text-right relative'
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              className='p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-all focus:opacity-100'
                              onClick={(e) => {
                                e.stopPropagation()
                                if (activeDropdown === c.id) {
                                  setActiveDropdown(null)
                                  setDropdownPos(null)
                                } else {
                                  setDropdownPos(
                                    getDropdownPosition(e.currentTarget as HTMLElement)
                                  )
                                  setActiveDropdown(c.id)
                                }
                              }}
                            >
                              <MoreHorizontal className='w-4 h-4' />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </main>
      </div>

      {c && (
        <AccountsActionDropdown open={true} position={dropdownPos}>
          {hasPermission(ACCOUNTS_PERMS.VENDOR_CREDIT_UPDATE) && (
            <button
              className='w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2'
              onClick={(e) => {
                e.stopPropagation()
                setActiveDropdown(null)
                setDropdownPos(null)
                navigate(`/accounts/purchase/vendor-credits/${c.id}`)
              }}
            >
              <Edit className='w-4 h-4 text-slate-400' /> Edit Credit
            </button>
          )}
          {hasPermission(ACCOUNTS_PERMS.VENDOR_CREDIT_DELETE) && (
            <>
              <div className='h-px bg-slate-100 my-1'></div>
              <button
                className='w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2'
                onClick={(e) => {
                  setActiveDropdown(null)
                  setDropdownPos(null)
                  handleDelete(e, c.id)
                }}
              >
                <Trash2 className='w-4 h-4 text-red-400' /> Delete Credit
              </button>
            </>
          )}
        </AccountsActionDropdown>
      )}
    </>
  )
}
